import bolide_assistant_composer from "./BolideAssistantComposer/BolideAssistantComposer.vue";
import bolide_assistant_header from "./BolideAssistantHeader/BolideAssistantHeader.vue";
import bolide_assistant_history from "./BolideAssistantHistory/BolideAssistantHistory.vue";
import bolide_assistant_messages from "./BolideAssistantMessages/BolideAssistantMessages.vue";
import chat_methods from "./bolide_assistant_chat_methods";
import conversation_methods from "./bolide_assistant_conversation_methods";
import history_methods from "./bolide_assistant_history_methods";
import message_methods from "./bolide_assistant_message_methods";
import query_methods from "./bolide_assistant_query_methods";
import stage_methods from "./bolide_assistant_stage_methods";
import thinking_methods from "./bolide_assistant_thinking_methods";

export default {
  name: "BolideAssistant",

  components: {
    BolideAssistantComposer: bolide_assistant_composer,
    BolideAssistantHeader: bolide_assistant_header,
    BolideAssistantHistory: bolide_assistant_history,
    BolideAssistantMessages: bolide_assistant_messages,
  },

  props: {
    is_open: Boolean,
    scope: {
      type: Object,
      default: null,
    },
    compare_property_options: {
      type: Array,
      default: () => [],
    },
    compare_property_ids: {
      type: Array,
      default: () => [],
    },
    standalone: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close-assistant", "update:compare_property_ids"],

  data() {
    return ({
      active_storage_key: "bolide_assist_active_conversation_v1",
      is_fullscreen: false,
      is_history_open: false,
      user_message: "",
      is_sending: false,
      typewriter_timer: null,
      conversations: [],
      active_conversation_id: null,
      chat_history_next_cursor: null,
      is_loading_chat_history: false,
      has_loaded_backend_history: false,
      chat_history_load_error: "",
      should_start_new_after_history_load: false,
    });
  },

  computed: {
    active_conversation() {
      return this.conversations.find(
        (item) => item.id === this.active_conversation_id
      ) || null;
    },

    active_conversation_title() {
      return this.active_conversation
        ? this.get_conversation_title(this.active_conversation)
        : "AI Dashboard Assistant";
    },

    messages() {
      return this.active_conversation?.messages || [];
    },

    composer_placeholder() {
      if (this.scope?.level === "property" && this.scope.property_name) {
        return `Ask about ${this.scope.property_name} - water, electricity, solar, battery...`;
      }
      if (this.scope?.level === "portfolio") {
        return "Ask across your portfolio, or pick properties to compare...";
      }
      return "Ask me anything...";
    },
  },

  watch: {
    messages: {
      deep: true,
      handler() {
        this.scroll_to_latest_message();
      },
    },
    active_conversation_id(id) {
      this.save_active_conversation(id);
      this.ensure_active_conversation_messages(id);
      this.scroll_to_latest_message({ settle: true });
    },
    is_open(is_open_state) {
      this.sync_dock_layout();

      if (is_open_state) {
        this.handle_assistant_open();
      }
    },
    is_fullscreen() {
      this.sync_dock_layout();
    },
  },

  mounted() {
    this.load_conversations();
    this.sync_dock_layout();
    window.addEventListener("resize", this.update_dock_top);

    // The panel can already be open when a page mounts (it stays open across
    // pages), so re-measure when the header appears, e.g. after a loader.
    const header = document.querySelector(".dashboard-header");
    if (header && window.ResizeObserver) {
      this.header_observer = new ResizeObserver(() => this.update_dock_top());
      this.header_observer.observe(header);
    }
  },

  beforeUnmount() {
    this.clear_typewriter_timer();
    this.header_observer?.disconnect();
    window.removeEventListener("resize", this.update_dock_top);
    document.body.classList.remove("chatbot-docked-open");
  },

  methods: {
    ...conversation_methods,
    ...history_methods,
    ...message_methods,
    ...thinking_methods,
    ...chat_methods,
    ...query_methods,
    ...stage_methods,

    close_assistant() {
      this.$emit("close-assistant");
    },

    toggle_fullscreen() {
      this.is_fullscreen = !this.is_fullscreen;
    },

    // The docked panel starts under the page header and, on wide screens,
    // pushes the page content left via the body class (see style.css).
    sync_dock_layout() {
      const is_docked_open = this.is_open && !this.is_fullscreen && !this.standalone;

      document.body.classList.toggle("chatbot-docked-open", is_docked_open);
      this.update_dock_top();
    },

    update_dock_top() {
      if (this.standalone || !this.$refs.chatbot) return;

      const header = document.querySelector(".dashboard-header");
      const header_bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

      this.$refs.chatbot.style.setProperty("--chatbot-top", `${header_bottom}px`);
    },

    toggle_history() {
      this.is_history_open = !this.is_history_open;
    },

    // In the docked panel the chat list is a drawer over the messages,
    // so it closes once a chat is picked. Fullscreen keeps it as a column.
    handle_select_conversation(conversation_id) {
      this.select_conversation(conversation_id);
      if (!this.is_fullscreen && !this.standalone) this.is_history_open = false;
    },

    open_in_new_tab() {
      // Same-origin window.open() copies the opener's sessionStorage (where
      // the auth token lives) into the new tab, so it lands already signed in.
      window.open("/assistant", "_blank");
    },

    async handle_assistant_open() {
      if (this.is_loading_chat_history) {
        this.should_start_new_after_history_load = true;
        return;
      }

      const reload_result = this.retry_load_conversations_if_needed();

      if (reload_result && typeof reload_result.then === "function") {
        this.should_start_new_after_history_load = true;
        await reload_result;
        return;
      }

      this.start_fresh_open_conversation();
    },

    start_fresh_open_conversation() {
      this.should_start_new_after_history_load = false;
      this.start_new_conversation({ reuse_empty_active: true });
      this.scroll_to_latest_message({ settle: true });
    },

    scroll_to_latest_message(options) {
      this.$nextTick(() => {
        this.$refs.chat_messages?.scroll_to_bottom(options);
      });
    },
  },
};
